import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { GridColDef, GridRowModel } from "@mui/x-data-grid";
import { useParams } from "react-router";
import { GET_SESSIONS_BY_FREQ } from "../../../features/graphql/query";
import { TablePaginated, Header, SearchBar, TableStandard, } from "ui";
import { startOfISOWeek, endOfISOWeek, addWeeks, format, getWeek, getYear } from "date-fns";
import { FetchMoreCallbackArgs } from "ui/components/Tables";
import { Box, FormControl, MenuItem, Select } from "@mui/material";
import { countTypeOfSession, getYearsBetweenDates } from "../tools/SessionTools";
const DEFAULT_PAGE_SIZE = 10;
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "weekRange", headerName: "Week", flex: 1, resizable: false,
    }, {
        field: "totalRun", headerName: "Complete Sessions Run", flex: 1, resizable: false,
        renderHeader: (params) => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "center", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>{params.colDef.headerName}</div>
        )
    },
    {
        field: "totalPartial", headerName: "Partial Sessions Run", flex: 1, resizable: false,
        renderHeader: (params) => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "center", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>{params.colDef.headerName}</div>
        )
    },
    // {
    //     field: "totalUnuasble", headerName: "Unusable Sessions", flex: 1, resizable: false,
    //     renderHeader: (params) => (
    //         <div style={{ whiteSpace: "break-spaces", textAlign: "center", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>{params.colDef.headerName}</div>
    //     )
    // }
]
interface WeekRange {
    weekNumber: number;
    year: number;
    start: Date;
    end: Date;
}
function getAllWeeksOfYear(year: number): WeekRange[] {
    const weeks: WeekRange[] = [];

    // ISO week 1 always contains Jan 4
    let weekStart = startOfISOWeek(new Date(year, 0, 4));

    let weekNumber = 1;
    while (weekStart.getFullYear() === year || weekNumber === 1) {
        const weekEnd = endOfISOWeek(weekStart);

        weeks.push({ weekNumber, start: weekStart, end: weekEnd, year: year });

        weekNumber++;
        weekStart = addWeeks(weekStart, 1);
    }

    return weeks;
}

// function mapDataToWeeks(sessionsByWeekFreq: any, existingWeeks: WeekRange[]) {
//     return existingWeeks.map(existingWeek => {
//         // Find session data for this week
//         const weekData = sessionsByWeekFreq.find((s: any) => s.week === existingWeek.weekNumber && existingWeek.year === s.year);
//         const year = sessionsByWeekFreq[0]?.year ?? new Date().getFullYear
//         const week = existingWeek.weekNumber
//         if (weekData) {
//             const totalCompleteSessions = countTypeOfSession({ sessionStatus: "Complete", sessions: weekData.sessions })
//             const totalPartialSessions = countTypeOfSession({ sessionStatus: "Partial", sessions: weekData.sessions })
//             const totalUnusableSessions = countTypeOfSession({ sessionStatus: "Unusable", sessions: weekData.sessions })

//             return {
//                 week: week,
//                 year: year,
//                 weekRange: `${format(new Date(existingWeek.start), "MM/dd")} - ${format(new Date(existingWeek.end), "MM/dd")}`,
//                 totalRun: totalCompleteSessions.length,
//                 totalPartial: totalPartialSessions.length,
//                 totalUnuasble: totalUnusableSessions.length,
//                 total: weekData.sessions.length,
//                 id: `${year}-${week}`, // str
//             }
//         } else {
//             return {
//                 week: week,
//                 year: year,
//                 weekRange: `${format(new Date(existingWeek.start), "MM/dd")} - ${format(new Date(existingWeek.end), "MM/dd")}`,
//                 totalRun: 0,
//                 totalPartial: 0,
//                 totalUnuasble: 0,
//                 total: 0,
//                 id: `${year}-${week}`, // str
//             };
//         }
//     })
// }
function mapDataToWeeks(
    sessionsByWeekFreq: any,
    existingWeeks: WeekRange[]
) {
    const currentDate = new Date();

    const filteredWeeks = existingWeeks.filter((week) => {
        const weekEnd = new Date(week.end);

        // include only weeks that have started/ended already
        return weekEnd <= currentDate;
    });

    return filteredWeeks.map((existingWeek) => {
        const weekData = sessionsByWeekFreq.find(
            (s: any) =>
                s.week === existingWeek.weekNumber &&
                existingWeek.year === s.year
        );

        const year =
            sessionsByWeekFreq[0]?.year ?? new Date().getFullYear();

        const week = existingWeek.weekNumber;

        if (weekData) {
            const totalCompleteSessions = countTypeOfSession({
                sessionStatus: "Complete",
                sessions: weekData.sessions,
            });

            const totalPartialSessions = countTypeOfSession({
                sessionStatus: "Partial",
                sessions: weekData.sessions,
            });

            const totalUnusableSessions = countTypeOfSession({
                sessionStatus: "Unusable",
                sessions: weekData.sessions,
            });

            return {
                week,
                year,
                weekRange: `${format(
                    new Date(existingWeek.start),
                    "MM/dd"
                )} - ${format(new Date(existingWeek.end), "MM/dd")}`,
                totalRun: totalCompleteSessions.length,
                totalPartial: totalPartialSessions.length,
                totalUnuasble: totalUnusableSessions.length,
                total: weekData.sessions.length,
                id: `${year}-${week}`,
            };
        }

        return {
            week,
            year,
            weekRange: `${format(
                new Date(existingWeek.start),
                "MM/dd"
            )} - ${format(new Date(existingWeek.end), "MM/dd")}`,
            totalRun: 0,
            totalPartial: 0,
            totalUnuasble: 0,
            total: 0,
            id: `${year}-${week}`,
        };
    });
}

function calculateAverageSessionPerWeek(sessions: any, currentWeek: number) {
    var totalCompleteSessions = 0;
    var totalPartialSessions = 0;
    var totalUnusableSessions = 0;
    for (var i = 0; i < sessions.length; i++) {
        if (sessions[i].week <= currentWeek) {
            totalCompleteSessions += sessions[i].totalRun;
            totalPartialSessions += sessions[i].totalPartial;
            totalUnusableSessions += sessions[i].totalUnuasble;
        }
    }
    var averageComplete = Math.round((totalCompleteSessions / currentWeek) * 100) / 100;
    var averagePartial = Math.round((totalPartialSessions / currentWeek) * 100) / 100;
    var averageUnusable = Math.round((totalUnusableSessions / currentWeek) * 100) / 100;
    return { averageComplete, averagePartial, averageUnusable }

}
export function SessionFreqTable({ companyStartDate }: { companyStartDate: Date | null }) {
    const { id } = useParams();
    const windowLocation = "company"
    const currentYear = getYear(new Date());
    const { data, loading, fetchMore } = useQuery(GET_SESSIONS_BY_FREQ, {
        variables: {
            searchCriteria: windowLocation,
            searchId: id ?? '',
            first: DEFAULT_PAGE_SIZE,
            frequency: "WEEK",
            query: "",
            sessionType: "ALL",
            after: null
        }
    })
    const [filterOptions, setFilterOptions] = useState<number[]>()
    useEffect(() => {
        const temp = companyStartDate ? getYearsBetweenDates(getYear(companyStartDate), currentYear) : getYearsBetweenDates(currentYear, currentYear);
        setFilterOptions(temp)
    }, [companyStartDate, currentYear])
    const [weeksOfSelectedYear, setWeeksOfSelectedYear] = useState<WeekRange[]>([])
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[]
        filteredRows: GridRowModel[],
        title: string,
        subtitle: string | undefined,
        status: 'NORMAL' | "FILTERING"
        averageSessions: {
            avgComplete: number,
            avgPartial: number,
            avgUnusable: number
        }
    }>({
        columns: [], rows: [], filteredRows: [], status: "NORMAL", title: "", subtitle: "", averageSessions: {
            avgComplete: 0,
            avgPartial: 0, avgUnusable: 0
        }
    })
    const handleSelectYear = (year: number) => {
        const newWeeks = getAllWeeksOfYear(year)
        setSelectedYear(year)
        setWeeksOfSelectedYear(newWeeks)
    }

    useEffect(() => {
        if (weeksOfSelectedYear.length > 0 && data)
            setPageTableState((prevState) => {
                const columns = [...TABLE_COLUMNS]
                const currentDate = new Date()
                const rows = mapDataToWeeks(data?.sessionsByFrequency, weeksOfSelectedYear)
                const { averageComplete, averagePartial, averageUnusable } = calculateAverageSessionPerWeek(rows, getWeek(currentDate))

                return {
                    ...prevState,
                    columns,
                    rows: rows,
                    averageSessions: {
                        avgComplete: averageComplete,
                        avgPartial: averagePartial,
                        avgUnusable: averageUnusable
                    }
                }
            })
    }, [data, loading, weeksOfSelectedYear])
    //run the current year on load
    useEffect(() => {
        console.log("the cureent year : ", currentYear, typeof (currentYear))
        handleSelectYear(currentYear)
    }, [currentYear])


    //PSEUDO FILTER, BUT REPURPOSING THE 
    const handleFilter = async (e: any) => {
        console.log("setting it: ", e)
        handleSelectYear(e.target.value)
        setSelectedYear(e.target.value)
    }
    useEffect(() => {
        console.log("the selected year is: ", selectedYear)
    }, [selectedYear])
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        await fetchMore({
            variables: {
                searchCriteria: windowLocation,
                searchId: id ?? "",
                first: variables.first ?? DEFAULT_PAGE_SIZE,
                query: variables.query ?? "",
                after: variables.after ?? null,
            }
        }).then((res) => {
            const rows = mapDataToWeeks(res?.data?.sessionsByFrequency, weeksOfSelectedYear)
            if (rows && rows.length > 0) {
                setPageTableState((prevState) => ({
                    ...prevState,
                    rows
                }))
            }
        })
    }
    return (<>
        <TableStandard loading={loading}
            autoHeight
            rows={pageTableState.rows}
            columns={pageTableState.columns}
            renderToolbar={() => (
                <>
                    <Header title={`Freq of Sessions by Week of ${selectedYear}`} titleSize="h5"
                        // subtitle={`Avg Completed: ${pageTableState.averageSessions.avgComplete} | Avg Partial: ${pageTableState.averageSessions.avgPartial} | Avg Unusable: ${pageTableState.averageSessions.avgUnusable}`} />
                        subtitle={`Avg Completed: ${pageTableState.averageSessions.avgComplete} | Avg Partial: ${pageTableState.averageSessions.avgPartial} `} />
                    <Box>
                        <SearchBar>
                            <Box component="span">
                                <FormControl fullWidth>
                                    <Select id="table-filter"
                                        value={selectedYear}
                                        defaultValue={selectedYear}
                                        onChange={handleFilter}
                                        sx={{ height: "40px" }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    maxHeight: 300
                                                }
                                            }
                                        }}
                                    >
                                        {filterOptions?.map((opt, i) => (
                                            <MenuItem key={`${opt}-${i}`} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </SearchBar>
                    </Box>

                </>
            )}

            // disableColumnSelector
            // pageInfo={data?.sessionsByFrequency?.pageInfo}
            // totalRows={10}
            pageSize={DEFAULT_PAGE_SIZE}
            disableBoxShadow
        // onFetchMore={handleFetchMore}
        />
    </>
    )
}